import { Component, OnInit } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as CryptoJS from 'crypto-js';
import { LoadingSpinnerService } from 'src/app/services/loading-spinner.service';
import { environment } from 'src/environments/environment';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { getFirestore, Firestore, collection, getDocs } from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

  email: string = '';
  password: string = '';
  rememberMe: boolean = false;
  errorMessage: string = '';
  passwordFieldType: string = 'password';
  isAdmin: boolean = false;
  businesses: any[] = [];
  selectedBusinessId: string = '';
  filteredBusinesses: any[] = [];
  userAccessToken: string = '';
  userRole: string = '';
  isOrdersAdmin: boolean = false;

  constructor(
    private afAuth: AngularFireAuth,
    private router: Router,
    private firestore: AngularFirestore,
    private http: HttpClient,
    public loadingSpinnerService: LoadingSpinnerService,
  ) { }

  ngOnInit(): void {
    const savedEmail = localStorage.getItem('email');
    if (savedEmail) {
      this.email = savedEmail;
      this.rememberMe = true;
    }
  }

  fetchAllBusinesses() {
    this.firestore.collection('Businesses').get().subscribe({
      next: (businessSnapshot) => {
        this.businesses = businessSnapshot.docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        this.filteredBusinesses = [...this.businesses]; // Initialize filteredBusinesses
        localStorage.setItem('playgrounds', JSON.stringify(this.businesses));
      },
      error: (error) => {
        console.error('Error fetching businesses:', error);
      }
    });
  }

  superAdminLoginToBusiness(selectedBusinessId: string) {
    this.loadingSpinnerService.show();
    if (!selectedBusinessId) {
      // this.loadingSpinnerService.hide();
      this.errorMessage = 'Please select a business.';
      return;
    }

    try {
      this.firestore
        .collection('Businesses')
        .doc(selectedBusinessId)
        .get()
        .subscribe({
          next: (businessSnapshot) => {
            if (businessSnapshot.exists) {
              localStorage.setItem('businessData', JSON.stringify(businessSnapshot.data()));
              this.storeAccessToken(this.userAccessToken);
            } else {
              this.errorMessage = 'Business does not exist.';
              this.loadingSpinnerService.hide();
            }
          },
          error: () => {
            this.errorMessage = 'Error fetching business data.';
            this.loadingSpinnerService.hide();
          }
        });

    } catch (error) {
      this.errorMessage = 'Unexpected error occurred.';
      this.loadingSpinnerService.hide();
    }
  }

  searchBusinesses(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input?.value.toLowerCase() || '';
    this.filteredBusinesses = this.businesses.filter((business) => {
      if (business.name) {
        return business.name.toLowerCase().includes(searchTerm)
      }
    }
    );
  }

  fetchPlaygroundsFromSecondary(): void {
    const userClientsRef = collection(this.db, 'user_clients');
    getDocs(userClientsRef).then((snapshot) => {
      const list = snapshot.docs.map((doc) => {
        const data = doc.data();
        const uid = data['uid'] ?? doc.id;
        return {
          id: uid,
          uid,
          name: data['name'] ?? data['fullName'] ?? '',
          businessImageUrl: data['businessImageUrl'] ?? '',
          ...data
        };
      });
      this.businesses = list;
      this.filteredBusinesses = [...list];
      localStorage.setItem('playgrounds', JSON.stringify(list));
      this.loadingSpinnerService.hide();
    }).catch((error) => {
      console.error('Error fetching playgrounds from secondary:', error);
      this.errorMessage = 'Error loading playgrounds.';
      this.loadingSpinnerService.hide();
    });
  }

  /**
   * For ordersAdmin: load all playgrounds, set them as defaults (mp + businessData),
   * then proceed directly to Home by storing the access token.
   */
  fetchPlaygroundsFromSecondaryAndContinue(userAccessToken: string): void {
    const userClientsRef = collection(this.db, 'user_clients');
    getDocs(userClientsRef).then((snapshot) => {
      const list = snapshot.docs.map((doc) => {
        const data = doc.data();
        const uid = data['uid'] ?? doc.id;
        return {
          id: uid,
          uid,
          name: data['name'] ?? data['fullName'] ?? '',
          businessImageUrl: data['businessImageUrl'] ?? '',
          ...data
        };
      });

      this.businesses = list;
      this.filteredBusinesses = [...list];
      localStorage.setItem('playgrounds', JSON.stringify(list));

      // Set mp to all playground UIDs so Orders Admin initially sees all playgrounds
      const allUids = list.map(p => p.uid);
      localStorage.setItem('mp', JSON.stringify(allUids));

      // Choose first playground as default businessData for Home / PlaygroundService
      if (list.length > 0) {
        const first = list[0] as any;
        const businessData = {
          id: first.uid,
          uid: first.uid,
          name: first.name,
          businessImageUrl: first.businessImageUrl,
        };
        localStorage.setItem('businessData', JSON.stringify(businessData));
      }

      this.storeAccessToken(userAccessToken);
    }).catch((error) => {
      console.error('Error fetching playgrounds from secondary for ordersAdmin:', error);
      this.errorMessage = 'Error loading playgrounds.';
      this.loadingSpinnerService.hide();
    });
  }

  onOrdersAdminSelectPlayground(playground: any): void {
    if (!playground?.uid) {
      this.errorMessage = 'Invalid playground selection.';
      return;
    }
    this.loadingSpinnerService.show();
    localStorage.setItem('mp', JSON.stringify([playground.uid]));
    const businessData = {
      id: playground.uid,
      uid: playground.uid,
      name: playground.name ?? playground.fullName ?? '',
      businessImageUrl: playground.businessImageUrl ?? '',
      ...playground
    };
    localStorage.setItem('businessData', JSON.stringify(businessData));
    this.storeAccessToken(this.userAccessToken);
  }

  private secondaryApp!: FirebaseApp;
  private db!: Firestore;
  async login() {
    this.errorMessage = '';
    try {
      this.loadingSpinnerService.show();
      await this.afAuth.setPersistence('local');
      const result = await this.afAuth.signInWithEmailAndPassword(this.email, this.password);
      const userAccessToken: any = await result.user?.getIdToken();
      this.userAccessToken = userAccessToken;

      // Secondary Firebase auth - Register or Login
      this.secondaryApp = initializeApp(environment.firebaseSecondary, 'gulli-orders');
      this.db = getFirestore(this.secondaryApp);
      const auth = getAuth(this.secondaryApp);
      
      // Try to register the user first
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, this.email, this.password);
        console.log('✅ Registered and logged in as:', userCredential.user.email);
      } catch (error: any) {
        // If user already exists, log them in instead
        if (error.code === 'auth/email-already-in-use') {
          try {
            const userCredential = await signInWithEmailAndPassword(auth, this.email, this.password);
            console.log('✅ Logged in as:', userCredential.user.email);
          } catch (loginError: any) {
            console.error('❌ Login failed:', loginError.message);
          }
        } else {
          console.error('❌ Registration failed:', error.message);
        }
      }

      // Fetch user data
      this.firestore
        .collection('BusinessUsers', ref => ref.where('email', '==', result.user?.email))
        .get()
        .subscribe({
          next: (userSnapshot: any) => {
            if (userSnapshot.size > 0) {
              const userData = userSnapshot.docs[0].data();
              const role = userData.role;
              this.userRole = role;
              this.isOrdersAdmin = role === 'ordersAdmin';
              localStorage.setItem('userRole', role);
              localStorage.setItem('countryName', userData.countryName);

              if (role === 'superAdmin') {
                this.isAdmin = true;
                this.fetchAllBusinesses();
                const availableFeatures = ['analytics', 'feed', 'notifications', 'vouchers', 'orders'];
                localStorage.setItem('af', JSON.stringify(availableFeatures));
                this.loadingSpinnerService.hide();
                return;
              } else if (role === 'admin' || role === 'manager') {
                this.fetchBusinessData(userData, userAccessToken);
                return;
              } else if (role === 'user') {
                this.fetchBusinessData(userData, userAccessToken);
              } else if (role === 'ordersAdmin') {
                localStorage.setItem('af', JSON.stringify(['ordersAdmin']));
                this.fetchPlaygroundsFromSecondaryAndContinue(userAccessToken);
                return;
              } else {
                this.errorMessage = 'Invalid user role.';
                this.loadingSpinnerService.hide();
              }
            } else {
              console.error("No user data found.");
              this.errorMessage = 'No user data found.';
              this.loadingSpinnerService.hide();
            }
          },
          error: (error) => {
            console.error("Error fetching user data:", error);
            this.errorMessage = 'Error fetching user data.';
            this.loadingSpinnerService.hide();
          }
        });
    } catch (error: any) {
      this.loadingSpinnerService.hide();
      // Clear credentials on login failure
      if (error && error.code) {
        switch (error.code) {
          case 'auth/user-not-found':
            this.errorMessage = 'No user found with this email.';
            break;
          case 'auth/wrong-password':
            this.errorMessage = 'Incorrect password.';
            break;
          case 'auth/invalid-email':
            this.errorMessage = 'Invalid email address.';
            break;
          default:
            this.errorMessage = 'Login failed. Please try again.';
        }
      }
    }
  }

  fetchBusinessesByIds(ids: string[]) {
    if (!ids || ids.length === 0) {
      this.businesses = [];
      this.filteredBusinesses = [];
      localStorage.setItem('playgrounds', JSON.stringify([]));
      return;
    }
    this.firestore.collection('Businesses', ref => ref.where('__name__', 'in', ids)).get().subscribe({
      next: (businessSnapshot) => {
        this.businesses = businessSnapshot.docs.map((doc: any) => ({
          id: doc.id,
          ...doc.data(),
        }));
        this.filteredBusinesses = [...this.businesses];
        localStorage.setItem('playgrounds', JSON.stringify(this.businesses));
      },
      error: (error) => {
        console.error('Error fetching businesses:', error);
      }
    });
  }

  togglePasswordVisibility() {
    this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
  }

  async storeAccessToken(userAccessToken: string) {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${userAccessToken}`,
      'Content-Type': 'application/json'
    });
    this.http.post(environment.accessTokenUrl, {}, { headers })
      .subscribe({
        next: (response: any) => {
          const encryptedAccessToken = CryptoJS.AES.encrypt(response.accessToken, '9diJh8ETujshkf8WKRuAopfqdDH9Jm7N').toString();
          localStorage.setItem('EAT', encryptedAccessToken);
          this.loadingSpinnerService.hide();
          this.router.navigate(['/home']).then(() => {
            window.location.reload();
          });
        },
        error: (error) => {
          console.error('Error fetching access token:', error);
        }
      });
  }

  async fetchBusinessData(userData: any, userAccessToken: string) {

    const managedPlaygroundIDs = userData.managedPlaygroundIDs || [];
    const managedBusinessesIDs = userData.managedBusinessesIDs || [];
    localStorage.setItem("mp", JSON.stringify(managedPlaygroundIDs));
    localStorage.setItem("mb", JSON.stringify(managedBusinessesIDs));
    const features = localStorage.getItem('userRole') === 'ordersAdmin' ? ['orders'] : (userData.availableFeatures || []);
    localStorage.setItem("af", JSON.stringify(features));

    // Fetch business data
    this.firestore
      .collection('Businesses', ref => ref.where('__name__', '==', userData.businessId))
      .get()
      .subscribe({
        next: (businessSnapshot: any) => {
          if (businessSnapshot.size > 0) {
            const businessData = businessSnapshot.docs[0].data();
            localStorage.setItem("businessData", JSON.stringify(businessData));
            this.storeAccessToken(userAccessToken);
          }
        },
        error: (error) => {
          console.error("Error fetching business data:", error);
          this.errorMessage = 'Error fetching business data.';
        }
      });
  }
}



























